import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

export const FILE_ACCEPT = ".pdf,.txt,.md,.csv,.json,.docx,.png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.ogg,.m4a,.mp4,.webm,.mov";
export const fileError = (error) => typeof error?.response?.data?.detail === "string"
  ? error.response.data.detail : "File transfer failed. Please try again.";

export function useAttachments(channel) {
  const [selected, setSelected] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const selectedRef = useRef([]);
  const mounted = useRef(true);
  const update = useCallback((files) => { selectedRef.current = files; setSelected(files); }, []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const remove = useCallback((id) => update(selectedRef.current.filter(f => f.file_id !== id)), [update]);
  useEffect(() => {
    const deleted = (event) => remove(event.detail);
    window.addEventListener("aria-file-deleted", deleted);
    return () => window.removeEventListener("aria-file-deleted", deleted);
  }, [remove]);
  const add = (file) => {
    if (busy.current) return;
    if (selectedRef.current.some(f => f.file_id === file.file_id)) return;
    if (selectedRef.current.length >= 4) { setError("You can attach up to 4 files per message."); return; }
    setError(""); update([...selectedRef.current, file]);
  };
  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (busy.current || !files.length) return;
    if (files.length + selectedRef.current.length > 4) { setError("You can attach up to 4 files per message."); return; }
    if (files.some(f => f.size > 25 * 1024 * 1024)) { setError("Each file must be 25 MB or smaller."); return; }
    if (files.some(f => !f.size)) { setError("Empty files cannot be uploaded."); return; }
    if (files.some(f => !FILE_ACCEPT.split(",").includes(`.${f.name.split(".").pop().toLowerCase()}`))) {
      setError("Unsupported file type. Choose a document, image, audio or video file."); return;
    }
    busy.current = true; setError("");
    setUploads(files.map(f => ({ name: f.name, progress: 0 })));
    const failures = [];
    for (const [index, file] of files.entries()) {
      const body = new FormData(); body.append("file", file); body.append("channel", channel);
      try {
        const { data } = await api.post("/files", body, { onUploadProgress: (e) => {
          if (mounted.current) setUploads(prev => prev.map((u, i) => i === index ? { ...u, progress: Math.min(99, Math.round((e.loaded / (e.total || file.size)) * 100)) } : u));
        } });
        if (mounted.current) update([...selectedRef.current, data]);
      } catch (e) { failures.push(`${file.name}: ${fileError(e)}`); }
      if (mounted.current) setUploads(prev => prev.map((u, i) => i === index ? { ...u, done: true } : u));
    }
    busy.current = false;
    if (mounted.current) { setUploads([]); setError(failures.join(" ")); }
  };
  return { selected, uploads, error, setError, upload, add, remove, uploading: uploads.length > 0,
    clear: () => { if (!busy.current) { update([]); setError(""); } } };
}